import React, { useState } from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import CommonContainer from '../../../common/CommonContainer';
import makeStyles from '@material-ui/core/styles/makeStyles';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Field, Form, Formik } from 'formik';
import LoadingSnackbar from '../../../common/LoadingSnackbar';
import CommonMultiSelectField from '../../../common/form/CommonMultiSelectField';
import {
  SEARCH_BUTTON_WIDTH,
  STANDARD_INPUT_HEIGHT,
  STANDARD_INPUT_RADIUS,
} from '../../../../const/chart-const';
import { BACKGROUND_WHITE, BLACK_COLOR, LIGHT_GREY_COLOR } from '../../../../const/color-const';
import CommonButton from '../../../common/CommonButton';
import CommonDivider from '../../../common/CommonDivider';
import { lighten } from '@material-ui/core/styles';
import { clearComparisonReports, fetchComparisonReports } from '../../../../actions/report-actions';
import RegularDataGrid from '../../../common/table/RegularDataGrid';
import DataGridCell from '../../../common/table/DataGridCell';
import { isNumber } from '../../../../utils/report-utils';
import { DATE_FIELD_TYPE } from '../../../../utils/form-utils';
import { formatTimestampAsDate } from '../../../../utils/dates-utils';
import { COMPARISON_REPORT_LIST, getDefinedHeaders } from './ComparisionReportConfig';
import CommonSelectField from '../../../common/form/CommonSelectField';
import DatePicker from '../../../common/form/DatePicker';
import NoDataAvailable from '../../../common/charts/NoDataAvailable';
import { getHeaderTranslationForStandardReports } from '../standard/StandardReportUtils';
import {
  SALES_PER_CATEGORY, SALES_PER_SUPPLIER,
} from '../standard/StandardReportConfig';
import ComparisonTable from './ComparisonTable';
import { LAST_MONTH } from '../../../../utils/dates-const';

const CUSTOM_INPUT_WIDTH = 240;
const useStyles = makeStyles(theme => ({
  salesHeader: {
    paddingTop: theme.spacing(3),
  },
  contentWrapper: {
    '& form': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center',
      },
    },
  },
  buttonWrapper: {
    marginTop: theme.spacing(1.5),
    '& button': {
      width: SEARCH_BUTTON_WIDTH,
    },
  },
  topFields: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    '& > div': {
      backgroundColor: theme.palette.type === 'light' ? lighten(BACKGROUND_WHITE, 0.7) : BLACK_COLOR,
      borderRadius: STANDARD_INPUT_RADIUS,
      height: STANDARD_INPUT_HEIGHT,
      width: CUSTOM_INPUT_WIDTH,
      margin: theme.spacing(2),
    },
  },
  fieldsWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    [theme.breakpoints.up('lg')]: {
      '& > div.dateTimePickerWrapper:nth-child(3)': {
        marginLeft: theme.spacing(2),
      },
      '& > div.dateTimePickerWrapper:nth-child(2)': {
        marginRight: theme.spacing(2),
      },
    },
    '& > div': {
      backgroundColor: theme.palette.type === 'light' ? lighten(BACKGROUND_WHITE, 0.7) : BLACK_COLOR,
      borderRadius: STANDARD_INPUT_RADIUS,
      border: `1px solid ${LIGHT_GREY_COLOR}`,
      height: STANDARD_INPUT_HEIGHT,
      width: CUSTOM_INPUT_WIDTH,
      margin: theme.spacing(1),
      justifyContent: 'center',
      alignItems: 'center',
      display: 'flex',
      '& label': {
        border: 'none',
        marginLeft: 10,
        '&:not([class*="MuiFormLabel-filled"]):not([class*="Mui-focused"])': {},
        '&:not([class*="MuiFormLabel-filled"])': {
          top: -10,
        },
      },
      '& > div': {
        width: '100%',
        '& p.Mui-error': {
          paddingLeft: 10,
        },
        '& input': {
          marginLeft: 10,
          border: 'none',
          height: '100%',
          width: '100%',
          padding: 0,
          fontSize: 14,
          [theme.breakpoints.up('md')]: {
            fontSize: 16,
          },
        },
      },
      '& div': {
        '&::before': {
          display: 'none',
        },
        '&::after': {
          display: 'none',
        },
      },
    },
    '& .commonSelectField': {
      border: 'none',
      '& label': {
        margin: 0,
      },
    },
  },
  tableWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'center',
    margin: theme.spacing(4, 0, 8, 0),
    '& > div': {
      margin: theme.spacing(1),
    },
  },
  rangeWrapper: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2),
  },
  reportWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiDataGrid-main': {
      '& .MuiDataGrid-renderingZone, .rendering-zone': {
        display: 'flex',
        flexDirection: 'column',
        '& .totalCategory': {
          fontWeight: 'bold',
          order: 1,
        },
      },
    },
  },
  noDataFound: {
    marginTop: theme.spacing(4),
    fontSize: 16,
  },
}));

const ComparisonReport = ({ machines, fetchComparisonReports, comparisonReports, clearComparisonReports }) => {
  const [reportName, setReportName] = React.useState(SALES_PER_CATEGORY.reportName);
  const [loadingProgress, setLoadingProgress] = useState(null);
  const machinesOptions = machines.map(machine => ({ label: machine.Name, value: machine.Id }));
  const isEmpty = (values) => Object.values(values).some((field) => field === '');
  const reportHeaders = () => comparisonReports?.flat(3)?.[0] ? Object.keys(comparisonReports.flat(3)[0]) : null;
  const renderComparisonTable = [SALES_PER_CATEGORY, SALES_PER_SUPPLIER];
  const isComparisonSupported = comparisonReports && renderComparisonTable.some(item => item.reportName === reportName);
  const START_OF_LAST_MONTH = LAST_MONTH.option.dateFrom;
  const END_OF_LAST_MONTH = LAST_MONTH.option.dateTo;
  const { formatMessage } = useIntl();
  const classes = useStyles();
  
  const handleReportNameChange = React.useCallback((newReportName) => {
    setReportName(newReportName);
  }, []);
  
  React.useEffect(() => {
    clearComparisonReports();
  }, [reportName, clearComparisonReports]);
  
  const DATE_FIELDS = [
    {
      name: 'DateFrom1',
      type: DATE_FIELD_TYPE,
      server: 'Fr.o.m.-datum',
      initialValue: START_OF_LAST_MONTH,
      invalidDateMessage: <FormattedMessage id="invalid.date.format" defaultMessage="Invalid date format" />,
      label: <FormattedMessage
        id="date.field.start.date.report"
        defaultMessage={`Start date - report {reportNumber}`}
        values={{ reportNumber: '1' }}
      />,
    },
    {
      name: 'DateTo1',
      type: DATE_FIELD_TYPE,
      server: 'T.o.m.-datum',
      invalidDateMessage: <FormattedMessage id="invalid.date.format" defaultMessage="Invalid date format" />,
      label: <FormattedMessage
        id="date.field.end.date.report"
        defaultMessage={`End date - report {reportNumber}`}
        values={{ reportNumber: '1' }}
      />,
    },
    {
      name: 'DateFrom2',
      type: DATE_FIELD_TYPE,
      server: 'Fr.o.m.-datum',
      invalidDateMessage: <FormattedMessage id="invalid.date.format" defaultMessage="Invalid date format" />,
      label: <FormattedMessage
        id="date.field.start.date.report"
        defaultMessage={`Start date - report {reportNumber}`}
        values={{ reportNumber: '2' }}
      />,
    },
    {
      name: 'DateTo2',
      type: DATE_FIELD_TYPE,
      server: 'T.o.m.-datum',
      invalidDateMessage: <FormattedMessage id="invalid.date.format" defaultMessage="Invalid date format" />,
      initialValue: END_OF_LAST_MONTH,
      label: <FormattedMessage
        id="date.field.end.date.report"
        defaultMessage={`End date - report {reportNumber}`}
        values={{ reportNumber: '2' }}
      />,
    },
  ];
  
  const renderComparisonReportFields = (fields) => {
    return fields.map((item, index) => {
      return <Field
        variant="standard"
        key={index}
        name={item.name}
        component={DatePicker}
        label={item.label}
        invalidDateMessage={item.invalidDateMessage}
      />;
    });
  };
  
  const noDataAvailable = (comparisonReports) => {
    return comparisonReports && comparisonReports.every(report => report[0].length === 0);
  };
  
  const transformInputs = (values) => {
    return [{
      dateFrom: values.DateFrom1,
      dateTo: values.DateTo1,
    },
      {
        dateFrom: values.DateFrom2,
        dateTo: values.DateTo2,
      }];
  };
  
  const handleSubmit = (values, formikHelpers) => {
    fetchComparisonReports(values.report, machinesOptions.map(option => option.value), transformInputs(values), formikHelpers, setLoadingProgress, values.machineIDs);
  };
  
  const renderTableRange = (report) => {
    return <div className={classes.rangeWrapper}>
      <p><FormattedMessage id="date.field.date.range" defaultMessage="Range" />:&nbsp;</p>
      <strong>{formatTimestampAsDate(report.date.dateFrom)} </strong> &nbsp;-&nbsp;
      <strong> {formatTimestampAsDate(report.date.dateTo)}</strong>
    </div>;
  };
  
  const getSortedData = comparisonReports && [...comparisonReports].sort(
    (a, b) => new Date(a[1].date.dateFrom) - new Date(b[1].date.dateFrom),
  );
  
  return (
    <CommonContainer hAlign="center">
      <h1 className={classes.salesHeader}>
        <FormattedMessage
          id="report.title.comparisons"
          defaultMessage="Sales comparisons"
        />
      </h1>
      <div className={classes.contentWrapper}>
        <Formik
          initialValues={{
            ...Object.fromEntries(DATE_FIELDS.map(value => [value.name, value.initialValue ? value.initialValue : ''])),
            machineIDs: [null],
            report: reportName,
          }}
          onSubmit={handleSubmit}>
          {({ isSubmitting, values, isValid, submitForm }) => {
            
            return (
              <Form>
                <LoadingSnackbar
                  progress={loadingProgress}
                  label={<FormattedMessage id="loading" defaultMessage="Loading" />}
                />
  
                <div className={classes.topFields}>
                  <Field name="machineIDs"
                         label={<FormattedMessage
                           id="field.cash.register"
                           defaultMessage="Cash Register"
                         />}
                         options={[...machinesOptions, {
                           label: <>- <FormattedMessage
                             id="all"
                             defaultMessage="All"
                           /> -</>, value: null,
                         }]}
                         component={CommonMultiSelectField}
                  />
    
                  <Field
                    name={'report'}
                    component={(props) => (
                      <CommonSelectField
                        {...props}
                        extraHandleChangeAction={handleReportNameChange}
                      />
                    )}
                    options={COMPARISON_REPORT_LIST(formatMessage)}
                  />
                </div>
  
                <div className={classes.fieldsWrapper}>
                  {renderComparisonReportFields(DATE_FIELDS)}
                </div>
  
                <div className={classes.buttonWrapper}>
                  <CommonButton
                    onClick={() => isValid && submitForm()}
                    label={<FormattedMessage
                      id="compare"
                      defaultMessage="Compare"
                    />}
                    disabled={isSubmitting || isEmpty(values)}
                  />
                </div>
  
                <CommonDivider />
  
                {isComparisonSupported && <ComparisonTable comparisonData={getSortedData} />}
  
                <div className={classes.tableWrapper}>
                  {reportHeaders() && getSortedData
                    .map((report, idx) => (
                      <div key={idx} className={classes.reportWrapper}>
                        {renderTableRange(report[1])}
                        <RegularDataGrid
                          columns={reportHeaders().map(header => ({
                            headerName: getHeaderTranslationForStandardReports(formatMessage, header),
                            field: header,
                            headerAlign: 'center',
                            width: 200,
                            hide: !getDefinedHeaders(values.report).includes(header),
                            valueGetter: (params) => isNumber(params.value) ? Number(params.value) : params.value,
                            renderCell: (params) => <DataGridCell header={header} value={params.value} />,
                          }))}
                          rows={report[0].map((report, index) => ({ ...report, id: index }))}
                        />
                      </div>
                    ))}
                </div>
  
                {noDataAvailable(comparisonReports) && <NoDataAvailable />}
              </Form>
            );
          }}
        </Formik>
      </div>
    </CommonContainer>
  );
};

ComparisonReport.propTypes = {
  machines: PropTypes.array,
  comparisonReports: PropTypes.array,
  fetchComparisonReports: PropTypes.func,
  clearComparisonReports: PropTypes.func,
};

const decorate = compose(
  connect(
    store => ({
      machines: store.user.machines,
      comparisonReports: store.report.comparisonReports,
    }),
    dispatch => bindActionCreators({
      fetchComparisonReports,
      clearComparisonReports,
    }, dispatch)),
);

export default decorate(ComparisonReport);